import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Rocket, Check, AlertCircle, Building2, Link2, Copy, Server, Unplug, RefreshCw, Ticket } from 'lucide-react';
import { api } from '../lib/api';
import { Card } from '../components/Card';
import { DiscordMemberPicker } from '../components/DiscordMemberPicker';

const DISCORD_ID_RE = /^\d{17,20}$/;

// Founder-only. Two jobs in one founder surface:
//   1. Onboard a new client — invite link + one-click provision (org ACTIVE,
//      owner appointed, billing on, server bound, starter credits, owner DM'd).
//   2. Disconnect an existing server — sever the guild↔org binding, orphan the
//      org with a 90-day grace TTL (data preserved, then auto-expires), freeing
//      the server for re-onboarding.
export function OnboardClient() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [ownerDiscordId, setOwnerDiscordId] = useState('');
  const [guildId, setGuildId] = useState('');
  const [credits, setCredits] = useState('50');
  const [inviteUrl, setInviteUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [confirmingOrgId, setConfirmingOrgId] = useState<string | null>(null);
  const [mintLabel, setMintLabel] = useState('');
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  // Bot-install invite link, built server-side (scopes + permission integer
  // live in one place — config is king). Public-safe probe, same as Expired.
  useEffect(() => {
    let live = true;
    fetch('/auth/config')
      .then(r => (r.ok ? r.json() : {}))
      .then((c: { botInviteUrl?: string }) => { if (live && c.botInviteUrl) setInviteUrl(c.botInviteUrl); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  // Servers the bot is in. boundOrgId === null → onboarding candidate; set → a
  // live connection that can be disconnected.
  const guilds = useQuery({
    queryKey: ['admin', 'guilds'],
    queryFn: () => api.admin.listGuilds(),
  });
  const allGuilds = guilds.data?.guilds ?? [];
  const candidates = allGuilds.filter(g => !g.boundOrgId);
  const connected = allGuilds.filter(g => g.boundOrgId);
  // Resolves a bound invite-link's guildId to a display name, using the same
  // bot-guild list Step 2/3 already fetch. Falls back to the raw ID below if
  // not found (bot got kicked after binding, or listGuilds is scoped
  // differently) — never silently blank.
  const guildNameById = new Map(allGuilds.map(g => [g.id, g.name]));

  // Single-use bot-invite links — an alternative to the static invite above,
  // for handing a specific client a link that only works once.
  const inviteLinks = useQuery({
    queryKey: ['admin', 'bot-invite-links'],
    queryFn: () => api.admin.listBotInviteLinks(),
  });
  const links = inviteLinks.data?.links ?? [];

  const mintLink = useMutation({
    mutationFn: () => api.admin.mintBotInviteLink({ label: mintLabel.trim() || undefined }),
    onSuccess: () => {
      setMintLabel('');
      queryClient.invalidateQueries({ queryKey: ['admin', 'bot-invite-links'] });
    },
  });

  const provision = useMutation({
    mutationFn: () => api.admin.provisionOrg({
      name: name.trim(),
      ownerDiscordId: ownerDiscordId.trim(),
      ...(guildId.trim() ? { primaryGuildId: guildId.trim() } : {}),
      starterCredits: Number(credits) || 0,
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'guilds'] }),
  });

  const disconnect = useMutation({
    mutationFn: (orgId: string) => api.admin.disconnectOrg(orgId),
    onSuccess: () => {
      setConfirmingOrgId(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'guilds'] });
    },
  });

  // Registers /ggportal INTO a guild (provision does this automatically; this is
  // the manual path — works on a server that isn't provisioned yet).
  const registerCmds = useMutation({
    mutationFn: (gid: string) => api.admin.registerGuildCommands(gid),
  });

  const copyInvite = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — the link is shown below for manual copy */
    }
  };

  // Same copy-to-clipboard pattern as copyInvite above, keyed per-link since
  // several rows in the list below each get their own copy button.
  const copyText = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedToken(key);
      setTimeout(() => setCopiedToken(prev => (prev === key ? null : prev)), 2000);
    } catch {
      /* clipboard blocked — the link is shown below for manual copy */
    }
  };

  // GET /admin/bot-invite-links doesn't echo the full redeem URL (only the
  // token) — rebuild it the same way the backend does: portal origin +
  // /auth/invite/:token. The frontend is served from the same origin as the
  // API (see apiFetch's `/portal${path}` base), so window.location.origin
  // matches the backend's PORTAL_URL.
  const inviteUrlFor = (token: string) => `${window.location.origin}/auth/invite/${token}`;

  const fmtTime = (ms: number) =>
    new Date(ms).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const ready =
    name.trim().length >= 2 &&
    DISCORD_ID_RE.test(ownerDiscordId.trim()) &&
    (!guildId.trim() || DISCORD_ID_RE.test(guildId.trim())) &&
    !provision.isPending;

  const done = provision.data;
  const inputClass =
    'w-full rounded-lg border border-iron-700 bg-iron-900 px-3 py-2 font-mono text-sm text-white placeholder:text-iron-500 focus:border-forge-500 focus:outline-none';

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <div className="mb-6 flex items-center gap-3">
        <Building2 className="h-7 w-7 text-forge-400" />
        <div>
          <h1 className="text-2xl font-bold text-white">Onboard a client</h1>
          <p className="font-mono text-xs text-iron-500">
            One click: org live · owner appointed · billing active · server linked · starter credits · owner DM'd.
          </p>
        </div>
      </div>

      {/* Step 1 — get the bot into their server */}
      <Card className="mb-4">
        <div className="mb-3 flex items-center gap-2">
          <Link2 className="h-4 w-4 text-forge-400" />
          <span className="font-display text-xs font-black uppercase tracking-widest text-iron-200">
            1 · Invite the bot
          </span>
        </div>
        <p className="mb-3 font-mono text-xs text-iron-400">
          Send this to the client. They open it, pick their server, authorise. They need <span className="text-iron-200">Manage Server</span>.
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!inviteUrl}
            onClick={copyInvite}
            className="ember flex items-center gap-2 border border-forge-500/60 bg-forge-600/30 px-3 py-2 font-display text-[11px] font-black uppercase tracking-[0.15em] text-white transition-all hover:bg-forge-500/40 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy invite link'}
          </button>
          {inviteUrl
            ? <code className="truncate font-mono text-[10px] text-iron-500">{inviteUrl}</code>
            : <span className="font-mono text-[10px] text-amber-400">invite link unavailable — DISCORD_APP_ID not resolved</span>}
        </div>
      </Card>

      {/* Step 1b — mint a single-use invite link (alt to the static link above) */}
      <Card className="mb-4">
        <div className="mb-3 flex items-center gap-2">
          <Ticket className="h-4 w-4 text-forge-400" />
          <span className="font-display text-xs font-black uppercase tracking-widest text-iron-200">
            1b · One-time invite link
          </span>
        </div>
        <p className="mb-3 font-mono text-xs text-iron-400">
          Mint a link for one specific client instead — it works once, then shows "already used". Expires after 24h.
        </p>
        <div className="mb-3 flex items-center gap-2">
          <input
            type="text" value={mintLabel} onChange={e => setMintLabel(e.target.value)}
            placeholder="Label (optional) — e.g. FlowState onboarding call" maxLength={80}
            className={inputClass}
          />
          <button
            type="button"
            disabled={mintLink.isPending}
            onClick={() => mintLink.mutate()}
            className="ember flex shrink-0 items-center gap-2 border border-forge-500/60 bg-forge-600/30 px-3 py-2 font-display text-[11px] font-black uppercase tracking-[0.15em] text-white transition-all hover:bg-forge-500/40 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Ticket className="h-3.5 w-3.5" />
            {mintLink.isPending ? 'Minting…' : 'Mint one-time link'}
          </button>
        </div>

        {mintLink.isError && (
          <div className="mb-3 flex items-start gap-2 font-mono text-xs text-red-300">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{(mintLink.error as Error).message}</span>
          </div>
        )}

        {mintLink.data && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-emerald-800/60 bg-iron-900 px-3 py-2">
            <button
              type="button"
              onClick={() => copyText(mintLink.data.url, mintLink.data.token)}
              className="flex shrink-0 items-center gap-1.5 rounded-md border border-emerald-600 bg-emerald-700/30 px-2.5 py-1 font-display text-[10px] font-black uppercase tracking-wider text-emerald-200 transition-all hover:bg-emerald-600/40"
            >
              {copiedToken === mintLink.data.token ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copiedToken === mintLink.data.token ? 'Copied' : 'Copy link'}
            </button>
            <code className="min-w-0 truncate font-mono text-[10px] text-iron-400">{mintLink.data.url}</code>
            <span className="shrink-0 font-mono text-[10px] text-iron-500">expires {fmtTime(mintLink.data.expiresAt)}</span>
          </div>
        )}

        {links.length > 0 && (
          <div className="space-y-1.5 border-t border-iron-800 pt-3">
            {links.map(link => {
              // Three states: bound (claimed — the callback in Phase 2 atomically
              // recorded which guild redeemed it), expired-unclaimed (dead, but
              // looks identical to "still live" unless we check expiresAt
              // ourselves — founders shouldn't copy/share a dead link), or still
              // live (unused, not yet expired, copyable).
              const bound = link.boundGuildId !== null;
              const expired = !bound && link.expiresAt <= Date.now();
              const guildName = link.boundGuildId ? guildNameById.get(link.boundGuildId) : undefined;
              return (
                <div
                  key={link.token}
                  className={`flex items-center justify-between gap-2 rounded-lg border border-iron-700 bg-iron-900 px-3 py-2 ${bound || expired ? 'opacity-50' : ''}`}
                >
                  <div className="min-w-0 font-mono text-xs">
                    <div className="truncate text-iron-200">{link.label || '(no label)'}</div>
                    <div className="truncate text-[10px] text-iron-500">issued {fmtTime(link.issuedAt)}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {bound ? (
                      <span className="font-mono text-[10px] text-iron-500">
                        ✓ claimed by {guildName ? `"${guildName}"` : `server ${link.boundGuildId}`}
                        {link.boundAt ? ` at ${fmtTime(link.boundAt)}` : ''}
                      </span>
                    ) : expired ? (
                      <span className="font-mono text-[10px] text-amber-500">✕ expired {fmtTime(link.expiresAt)}</span>
                    ) : (
                      <>
                        <span className="font-mono text-[10px] text-emerald-300">● unused</span>
                        <button
                          type="button"
                          onClick={() => copyText(inviteUrlFor(link.token), link.token)}
                          className="flex items-center gap-1.5 rounded-md border border-iron-600 px-2.5 py-1 font-mono text-[10px] text-iron-300 transition-all hover:border-forge-600 hover:text-white"
                        >
                          {copiedToken === link.token ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                          {copiedToken === link.token ? 'Copied' : 'Copy'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Step 2 — provision */}
      <Card className="mb-4">
        <div className="mb-3 flex items-center gap-2">
          <Rocket className="h-4 w-4 text-forge-400" />
          <span className="font-display text-xs font-black uppercase tracking-widest text-iron-200">
            2 · Provision the org
          </span>
        </div>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-iron-400">
              Organisation name
            </label>
            <input
              type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="FlowState" className={inputClass}
            />
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-iron-400">
              Owner Discord ID
            </label>
            <DiscordMemberPicker
              value={ownerDiscordId}
              onChange={setOwnerDiscordId}
              search={q => api.admin.searchGuildMembers(guildId, q)}
              disabled={!guildId}
              disabledHint="Select a server first"
              placeholder="Type the owner's name…"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-iron-400">
                Discord server ID <span className="normal-case text-iron-500">(optional)</span>
              </label>
              <input
                type="text" value={guildId} onChange={e => setGuildId(e.target.value)}
                placeholder="binds their server to the org" className={inputClass}
              />
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-iron-400">
                Starter credits
              </label>
              <input
                type="number" min={0} max={10000} value={credits}
                onChange={e => setCredits(e.target.value)} className={inputClass}
              />
            </div>
          </div>

          {/* Server picker — bot's guilds that still need an org */}
          {candidates.length > 0 && (
            <div>
              <div className="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-iron-400">
                <Server className="h-3 w-3" /> Servers the bot is in (no org yet)
              </div>
              <div className="flex flex-wrap gap-2">
                {candidates.map(g => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => setGuildId(g.id)}
                    className={`rounded-md border px-2.5 py-1 font-mono text-[11px] transition-all ${
                      guildId === g.id
                        ? 'border-forge-500 bg-iron-800 text-white'
                        : 'border-iron-700 bg-iron-900 text-iron-300 hover:border-forge-600'
                    }`}
                  >
                    {g.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            type="button"
            disabled={!ready}
            onClick={() => provision.mutate()}
            className="ember flex w-full items-center justify-center gap-2 border border-forge-500/60 bg-forge-600/30 px-4 py-3 font-display text-xs font-black uppercase tracking-[0.18em] text-white transition-all hover:bg-forge-500/40 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Rocket className="h-4 w-4" strokeWidth={2.5} />
            {provision.isPending ? 'Provisioning…' : 'Provision org'}
          </button>

          {/* Register /ggportal into the entered/selected server without provisioning
              — handy to test command visibility, or if auto-register needs a re-run. */}
          {DISCORD_ID_RE.test(guildId.trim()) && (
            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                disabled={registerCmds.isPending}
                onClick={() => registerCmds.mutate(guildId.trim())}
                className="flex items-center gap-1.5 rounded-md border border-iron-600 px-2.5 py-1 font-mono text-[11px] text-iron-300 transition-all hover:border-forge-600 hover:text-white disabled:opacity-40"
              >
                <RefreshCw className={`h-3 w-3 ${registerCmds.isPending ? 'animate-spin' : ''}`} />
                {registerCmds.isPending ? 'Registering…' : 'Register /ggportal in this server'}
              </button>
              {registerCmds.data?.registered && <span className="font-mono text-[10px] text-emerald-300">/ggportal registered</span>}
              {registerCmds.isError && <span className="font-mono text-[10px] text-red-300">{(registerCmds.error as Error).message}</span>}
            </div>
          )}
        </div>
      </Card>

      {/* Step 3 — disconnect a connected server */}
      {connected.length > 0 && (
        <Card className="mb-4">
          <div className="mb-3 flex items-center gap-2">
            <Unplug className="h-4 w-4 text-forge-400" />
            <span className="font-display text-xs font-black uppercase tracking-widest text-iron-200">
              3 · Connected servers
            </span>
          </div>
          <p className="mb-3 font-mono text-xs text-iron-400">
            Disconnect severs the server's link and <span className="text-iron-200">orphans the org</span> —
            data preserved for 90 days, then auto-expires. The server is freed for re-onboarding.
          </p>
          <div className="space-y-2">
            {connected.map(g => {
              const orgId = g.boundOrgId as string;
              const confirming = confirmingOrgId === orgId;
              const busy = disconnect.isPending && disconnect.variables === orgId;
              return (
                <div key={g.id} className="flex items-center justify-between rounded-lg border border-iron-700 bg-iron-900 px-3 py-2">
                  <div className="min-w-0 font-mono text-xs">
                    <div className="truncate text-white">{g.name}</div>
                    <div className="truncate text-[10px] text-iron-500">org: {orgId} · server {g.id}</div>
                  </div>
                  {confirming ? (
                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => disconnect.mutate(orgId)}
                        className="rounded-md border border-red-600 bg-red-700/40 px-2.5 py-1 font-display text-[10px] font-black uppercase tracking-wider text-white transition-all hover:bg-red-600/50 disabled:opacity-40"
                      >
                        {busy ? 'Disconnecting…' : 'Confirm disconnect'}
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setConfirmingOrgId(null)}
                        className="font-mono text-[10px] text-iron-400 hover:text-iron-200"
                      >
                        cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        disabled={registerCmds.isPending && registerCmds.variables === g.id}
                        onClick={() => registerCmds.mutate(g.id)}
                        className="flex items-center gap-1.5 rounded-md border border-iron-600 px-2.5 py-1 font-display text-[10px] font-black uppercase tracking-wider text-iron-300 transition-all hover:border-forge-600 hover:text-white disabled:opacity-40"
                      >
                        <RefreshCw className={`h-3 w-3 ${registerCmds.isPending && registerCmds.variables === g.id ? 'animate-spin' : ''}`} /> Sync /ggportal
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmingOrgId(orgId)}
                        className="flex items-center gap-1.5 rounded-md border border-iron-600 px-2.5 py-1 font-display text-[10px] font-black uppercase tracking-wider text-iron-300 transition-all hover:border-red-600 hover:text-red-300"
                      >
                        <Unplug className="h-3 w-3" /> Disconnect
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {disconnect.isError && (
            <div className="mt-2 flex items-start gap-2 font-mono text-xs text-red-300">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{(disconnect.error as Error).message}</span>
            </div>
          )}
          {disconnect.data && (
            <div className="mt-2 font-mono text-[11px] text-emerald-300">
              Disconnected {disconnect.data.orgId} — server {disconnect.data.freedGuildId ?? '(none)'} freed; org orphaned, expires in {disconnect.data.graceDays} days.
            </div>
          )}
        </Card>
      )}

      {provision.isError && (
        <Card className="mb-4 border-red-800/60">
          <div className="flex items-start gap-2 text-sm text-red-300">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{(provision.error as Error).message}</span>
          </div>
        </Card>
      )}

      {done && (
        <Card className="border-emerald-800/60">
          <div className="mb-2 flex items-center gap-2 text-emerald-300">
            <Check className="h-5 w-5" />
            <span className="font-display text-sm font-black uppercase tracking-widest">
              {done.name} is live
            </span>
          </div>
          <ul className="space-y-1 font-mono text-xs text-iron-300">
            <li>org id: <span className="text-white">{done.orgId}</span></li>
            <li>owner: <span className="text-white">{done.ownerDiscordId}</span> (ORG OWNER)</li>
            <li>billing: <span className="text-emerald-300">{done.entitlementStatus}</span></li>
            <li>server: {done.primaryGuildId
              ? <span className="text-emerald-300">bound — {done.primaryGuildId}</span>
              : <span className="text-iron-500">not linked yet (bind later from the org page)</span>}</li>
            <li>starter credits: <span className="text-white">{done.creditsGranted}</span></li>
            <li>owner DM: {done.dmSent
              ? <span className="text-emerald-300">sent — portal button delivered</span>
              : <span className="text-amber-300">failed (DMs closed?) — send them the portal link manually</span>}</li>
            <li>/ggportal command: {done.commandsRegistered
              ? <span className="text-emerald-300">registered in their server</span>
              : <span className="text-iron-500">{done.primaryGuildId ? 'not registered — use Sync /ggportal' : 'no server linked'}</span>}</li>
          </ul>
        </Card>
      )}
    </div>
  );
}
